// ─────────────────────────────────────────────────────────────────────────────
// Characters who live in the ocean and give quests in the class's language:
// the Old Turtle near the start, and the Ghost Captain by the first wreck. Their
// words are in lang.ts (npcLines); the quests are checked and rewarded by
// ocean_quest (supabase/migrations/20261022_ocean_adventure.sql), which has the
// same list: keep the two in step.
// ─────────────────────────────────────────────────────────────────────────────

export type NpcId = 'turtle' | 'captain';
export type QuestId = 'turtle1' | 'turtle2' | 'turtle3' | 'captain1' | 'captain2' | 'captain3';

/** What each quest needs and gives (shown with icons next to the words). */
export const QUESTS: Record<NpcId, { id: QuestId; item?: string; qty?: number; fish?: string; reward: Record<string, number> }[]> = {
  turtle: [
    { id: 'turtle1', item: 'sand', qty: 5, reward: { beacon: 1, coral: 5 } },
    { id: 'turtle2', fish: 'clownfish', reward: { pickaxe: 10, glass: 4 } },
    { id: 'turtle3', item: 'pearl', qty: 3, reward: { sword: 10, beacon: 1 } },
  ],
  captain: [
    { id: 'captain1', item: 'wood', qty: 6, reward: { planks: 8, lamp: 2 } },
    { id: 'captain2', fish: 'spiky-puffer', reward: { sword: 15, beacon: 1 } },
    { id: 'captain3', item: 'crystal', qty: 2, reward: { beacon: 2, pearl: 2, coral_brick: 6 } },
  ],
};

/** The next quest for this student, or null when they've done them all. */
export function nextQuest(npc: NpcId, done: Set<string>) {
  return QUESTS[npc].find(q => !done.has(q.id)) ?? null;
}

const INK = '#1e2633';

/** Draw an NPC standing at (x, y) (its base), bobbing gently; `ready` shows a "!" over it. */
export function drawNpc(g: CanvasRenderingContext2D, id: NpcId, x: number, y: number, t: number, ready: boolean, near: boolean) {
  g.save();
  g.translate(x, y + Math.sin(t * 1.4) * 3);
  g.lineJoin = 'round'; g.lineCap = 'round'; g.strokeStyle = INK; g.lineWidth = 3;
  if (id === 'turtle') {
    // An old sea turtle with a mossy shell and little glasses.
    g.fillStyle = '#7fbf6a';
    for (const [lx, ly] of [[-38, -6], [30, -4]] as [number, number][]) { g.beginPath(); g.ellipse(lx, ly, 16, 8, lx < 0 ? 0.4 : -0.4, 0, Math.PI * 2); g.fill(); g.stroke(); }
    g.beginPath(); g.ellipse(-56, -26, 18, 14, 0, 0, Math.PI * 2); g.fill(); g.stroke();
    g.fillStyle = '#6b8f4a';
    g.beginPath(); g.ellipse(0, -26, 46, 30, 0, Math.PI, 0); g.quadraticCurveTo(0, -8, -46, -26); g.closePath(); g.fill(); g.stroke();
    g.strokeStyle = 'rgba(30,38,51,0.5)'; g.lineWidth = 2;
    for (const px of [-22, 0, 22]) { g.beginPath(); g.moveTo(px - 10, -36); g.lineTo(px, -46); g.lineTo(px + 10, -36); g.lineTo(px, -26); g.closePath(); g.stroke(); }
    g.fillStyle = '#4c8a3c'; g.beginPath(); g.ellipse(14, -52, 10, 4, 0.3, 0, Math.PI * 2); g.fill();
    g.strokeStyle = INK; g.lineWidth = 2;
    g.fillStyle = '#fff'; g.beginPath(); g.arc(-62, -30, 5, 0, Math.PI * 2); g.fill(); g.stroke();
    g.fillStyle = INK; g.beginPath(); g.arc(-63, -30, 2.2, 0, Math.PI * 2); g.fill();
    g.beginPath(); g.arc(-62, -30, 8, 0, Math.PI * 2); g.stroke();
    g.beginPath(); g.moveTo(-70, -22); g.quadraticCurveTo(-64, -18, -58, -21); g.stroke();
  } else {
    // A ghostly captain: a see-through fish in a tricorn hat.
    g.globalAlpha = 0.72 + Math.sin(t * 2) * 0.12;
    g.fillStyle = '#cfe8ff';
    g.beginPath(); g.moveTo(-40, -40); g.bezierCurveTo(-40, -96, 40, -96, 40, -40);
    for (let i = 0; i <= 6; i++) g.lineTo(40 - i * (80 / 6), -10 + (i % 2 ? -10 : 0) + Math.sin(t * 3 + i) * 3);
    g.closePath(); g.fill(); g.stroke();
    g.fillStyle = '#1e2633';
    g.beginPath(); g.moveTo(-44, -78); g.quadraticCurveTo(0, -112, 44, -78); g.quadraticCurveTo(0, -90, -44, -78); g.fill();
    g.fillStyle = '#ffd23d'; g.beginPath(); g.arc(0, -92, 4, 0, Math.PI * 2); g.fill();
    g.fillStyle = INK;
    g.beginPath(); g.ellipse(-14, -56, 6, 8, 0, 0, Math.PI * 2); g.fill();
    g.beginPath(); g.ellipse(14, -56, 6, 8, 0, 0, Math.PI * 2); g.fill();
    g.beginPath(); g.ellipse(0, -38, 8, 5, 0, 0, Math.PI * 2); g.fill();
    g.globalAlpha = 1;
  }
  if (ready) {
    const by = id === 'turtle' ? -86 : -128;
    g.fillStyle = '#ffd23d'; g.strokeStyle = INK; g.lineWidth = 2.5;
    g.beginPath(); g.arc(0, by, 13, 0, Math.PI * 2); g.fill(); g.stroke();
    g.fillStyle = INK; g.font = '800 18px system-ui'; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText('!', 0, by + 1);
  }
  g.restore();
  if (near) {
    g.save();
    g.strokeStyle = 'rgba(255,255,255,0.85)'; g.lineWidth = 3; g.setLineDash([6, 6]); g.lineDashOffset = -t * 20;
    g.beginPath(); g.arc(x, y - 40, 62, 0, Math.PI * 2); g.stroke();
    g.restore();
  }
}
